// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import Loader from "src/components/shared/loaders/Loader/Loader";
import ProgressBar from "src/components/shared/displays/ProgressBar/ProgressBar";
import { CheckCircleIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Utils
import {
  getExamPackageText,
  getExamSectionEye,
  getExamSectionText,
  getReportExamsSortedByGroupId,
} from "src/utils/exams/examReportsHelper";

// Types
import { ExamSectionType } from "src/services/exams/types";

type PackageInfoType = {
  isExpanded: boolean;
};

const PackageInfo = ({ isExpanded }: PackageInfoType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { exam, currentExamSection } = useLiveUpdateContext();

  const allSortedSections = useMemo(() => {
    if (exam) {
      return getReportExamsSortedByGroupId(exam);
    }

    return [];
  }, [exam]);

  const percentageCompleted = useMemo(() => {
    const numOfSections = allSortedSections?.length ?? 0;
    const numOfCompletedSections =
      allSortedSections?.filter((section) => Boolean(section?.completionDate))
        ?.length ?? 0;

    return Math.floor((numOfCompletedSections / numOfSections) * 100);
  }, [allSortedSections]);

  const isCompleteExamSection = useCallback(
    (section: ExamSectionType) => Boolean(section?.completionDate),
    []
  );

  const isCurrentExamSection = useCallback(
    (section: ExamSectionType) => currentExamSection?.id === section?.id,
    [currentExamSection]
  );

  const getSectionColor = useCallback(
    (section: ExamSectionType) => {
      if (isCompleteExamSection(section)) return "#22BE39";
      if (isCurrentExamSection(section)) return "#2563EB";
      return "#5B6D7A";
    },
    [isCompleteExamSection, isCurrentExamSection]
  );

  const getSectionIcon = useCallback(
    (section: ExamSectionType, index: number) => {
      let icon = (
        <Box
          width="16px"
          height="16px"
          minWidth="16px"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#E5E9ED95",
            borderRadius: "50%",
          }}
        >
          <Typography fontSize="10px" color={onSurface}>
            {index}
          </Typography>
        </Box>
      );

      if (isCurrentExamSection(section)) {
        icon = (
          <Box>
            <Loader
              size="14px"
              color="#2563EB"
              containerProps={{ width: "16px", height: "16px" }}
            />
          </Box>
        );
      } else if (isCompleteExamSection(section)) {
        icon = (
          <Box color="#22BE39">
            <CheckCircleIcon width="16px" height="16px" />
          </Box>
        );
      }

      return (
        <Tooltip
          placement="right"
          title={!isExpanded ? getExamSectionText(section, t) : ""}
        >
          {icon}
        </Tooltip>
      );
    },
    [isCurrentExamSection, isCompleteExamSection, isExpanded, t, onSurface]
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <ProgressBar
        value={percentageCompleted}
        displayValue={isExpanded}
        variant="small"
      />

      <Box display="flex" flexDirection="column" gap="8px">
        {allSortedSections?.map((examSection, index) => (
          <Box
            key={examSection?.id}
            display="flex"
            alignItems="flex-start"
            gap="8px"
          >
            {getSectionIcon(examSection, index + 1)}
            {isExpanded && (
              <Box display="flex" flexDirection="column">
                <Typography
                  color={getSectionColor(examSection)}
                  fontSize="10px"
                  fontWeight={600}
                >
                  {getExamSectionEye(examSection, t)}
                </Typography>
                <Typography
                  fontSize="10px"
                  color={getSectionColor(examSection)}
                >
                  {getExamPackageText(examSection, t)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PackageInfo;
